import { atom, selector, selectorFamily, waitForAll } from 'recoil'
import { NearVenue } from 'src/common-services'
import { findPlaceDetails, GooglePlace } from 'src/utils/google-places'
import { DEFAULT_LRU_SIZE, keyFactory, uncachedSelector } from 'src/utils/recoil'
import { findVenueForGooglePlace } from 'src/atoms/search'

const key = keyFactory('compare')

export const EXTRA_GOOGLE_PLACE_IDS_LIMIT = 3

export const modalSelectedIndex = atom<number | null>({
  key: key('modal-selected-index'),
  default: null,
})

export const modalSelectedGooglePlaceId = atom<string | undefined>({
  key: key('modal-selected-google-place-id'),
  default: undefined,
})

export const modalSelectedGooglePlace = uncachedSelector<GooglePlace | null | undefined>({
  key: key('modal-selected-google-place'),
  get: ({ get }) => {
    const placeId = get(modalSelectedGooglePlaceId)
    if (!placeId) {
      return undefined
    }
    return findPlaceDetails(placeId)
  },
  lru: DEFAULT_LRU_SIZE,
})

export const googlePlaceIds = atom<string[]>({
  key: key('google-place-ids'),
  default: [],
})

export const amount = selector<number>({
  key: key('amount'),
  get: ({ get }) => {
    const placeIds = get(googlePlaceIds)
    return placeIds.length
  },
})

export const googleSearchString = atom<string | undefined>({
  key: key('google-search-string'),
  default: undefined,
})

export const basicGooglePlace = selectorFamily<GooglePlace | null | undefined, GooglePlace['place_id']>({
  key: key('basic-google-place'),
  get:
    (placeId: GooglePlace['place_id']) =>
    ({ get }) => {
      return findPlaceDetails(placeId, true)
    },
})

export function notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
  return value !== null && value !== undefined
}

export const googlePlaces = uncachedSelector({
  key: key('google-places'),
  get: async ({ get }) => {
    const placeIds = get(googlePlaceIds)
    const placesAndNulls = get(waitForAll(placeIds.map((id) => basicGooglePlace(id))))
    return placesAndNulls.filter(notEmpty)
  },
  lru: DEFAULT_LRU_SIZE,
})

export const venues = uncachedSelector<Array<NearVenue | undefined>>({
  key: key('venues'),
  get: async ({ get }) => {
    const places = get(googlePlaces)
    if (!places?.length) {
      return []
    }
    const venues = await Promise.all(places.map((place) => findVenueForGooglePlace(place)))
    return venues.filter((v) => v !== undefined)
  },
  lru: DEFAULT_LRU_SIZE,
})
